import React, { Suspense, lazy } from "react";
import { useRoutes } from "react-router-dom";
import Loader from "../layout/loader";
import { appRoutes } from "../constant";
import PrivateRoutes from "../utilities/PrivateRoutes";
import KYCBackup from "../components/documents/kyc/kycBackup";
import AssignedProductsToClient from "../components/affiliations/clients/companies/assigned-products";
import AssignedDealershipsToClient from "../components/affiliations/clients/dealerships/assigned-dealerships";
import AdminWalletHistory from "../components/wallet/admin-wallet";
import EAWalletHistory from "../components/wallet/ea-wallet";
import { getFromLocalStorage } from "../hooks/useLocalStorage.js";
import AccessDenied from "../components/common/AccessDenied.jsx";
import checkClientConfig from "../utilities/checkClientConfig";

function checkRoleAccess(allowedRoles, component) {
  const ROLE = getFromLocalStorage("ROLE")?.key;
  if (allowedRoles?.includes(ROLE)) {
    return component;
  } else {
    return <AccessDenied />;
  }
}

function DealerQRCodeRouteGuard({ children }) {
  const isDealershipManager =
    getFromLocalStorage("ROLE")?.key === "DEALERSHIP_MANAGER";
  const isDealerQRCodeEnabled = checkClientConfig("DEALER_QR_CODE");

  return isDealershipManager && isDealerQRCodeEnabled
    ? children
    : <AccessDenied />;
}

// eslint-disable-next-line react/display-name
const Loadable = (Component) => (props) => {
  return (
    <Suspense fallback={<Loader />}>
      <Component {...props} />
    </Suspense>
  );
};
function ApplicationRoutes() {
  return useRoutes([
    {
      path: "",
      children: [
        { path: appRoutes.LOGIN, element: <Signin /> },
        { path: appRoutes.REMEMBER_ME, element: <RememberMe /> },
        { path: appRoutes.AUTH_LOGIN, element: <Login /> },
        { path: appRoutes.CALLBACK_LOGIN, element: <CallBackLogin /> },
        {
          path: appRoutes.AUTH_LOGIN_LOGIN_WITH_IMG1,
          element: <LoginWithBgImage />,
        },
        {
          path: appRoutes.AUTH_LOGIN_LOGIN_WITH_IMG2,
          element: <LoginWithBgVideo />,
        },
        {
          path: appRoutes.AUTH_LOGIN_WITH_VALIDATION,
          element: <LoginWithValidation />,
        },
        { path: appRoutes.AUTH_SIGNUP, element: <Register /> },
        {
          path: appRoutes.AUTH_SIGNUP_WITH_IMG1,
          element: <RegisterWithBgImage />,
        },
        {
          path: appRoutes.AUTH_SIGNUP_WITH_IMG2,
          element: <RegisterWithBgVideo />,
        },
        { path: appRoutes.AUTH_FORGOT_PASSWORD, element: <Forgetpwd /> },
        { path: appRoutes.AUTH_UNLOCK_USER, element: <UnlockUser /> },
        { path: appRoutes.AUTH_RESET_PASSWORD, element: <Resetpwd /> },
        { path: appRoutes.ERRORS_ERROR400, element: <Error400 /> },
        { path: appRoutes.ERRORS_ERROR401, element: <Error401 /> },
        { path: appRoutes.ERRORS_ERROR403, element: <Error403 /> },
        { path: appRoutes.ERRORS_ERROR404, element: <Error404 /> },
        { path: appRoutes.ERRORS_ERROR500, element: <Error500 /> },
        { path: appRoutes.ERRORS_ERROR503, element: <Error503 /> },
        { path: appRoutes.COMMING_SOON, element: <Comingsoon /> },
        { path: appRoutes.COMMING_SOON_IMG, element: <ComingsoonImg /> },
        { path: appRoutes.COMMING_SOON_VIDEO, element: <ComingsoonVideo /> },
        { path: appRoutes.MAINTAINANCE, element: <Maintenance /> },
        { path: appRoutes.CALLBACK, element: <Callback /> },
        { path: appRoutes.FORGOT_PASSWORD, element: <ForgotPassword /> },
        { path: appRoutes.OTP, element: <ValidateOTP /> },
        { path: appRoutes.MAIL_SENT, element: <MailSent /> },
        { path: appRoutes.RESET_PASSWORD, element: <ResetPassword /> },
        {
          path: appRoutes.USER_FIRST_TIME_LOGIN,
          element: <FirstLoginValidateOTP />,
        },
        { path: appRoutes.ALLOCATION, element: <Allocation /> },
        { path: appRoutes.HIERARCHY, element: <Hierarchy /> },
        { path: appRoutes.TARGET_ALLOTMENT, element: <TargetAlloment /> },
        { path: appRoutes.TARGET_PAGE, element: <TargetPage /> },
        {
          path: appRoutes.TEAM_MEMBER_DASHBOARD,
          element: <TeamMemberDashboard />,
        },
        { path: appRoutes.DEALER_DASHBOARD, element: <DealerDashboard /> },
        { path: appRoutes.CCM_DASHBOARD, element: <CcmDashboard /> },
        { path: appRoutes.DSP_DASHBOARD, element: <DspDashboard /> },
        { path: appRoutes.FINANCE_DASHBOARD, element: <FinanceDasbaord /> },
        { path: appRoutes.HOME, element: <Home /> },
        { path: appRoutes.VEHICLE_DETAILS, element: <vehicleDetails /> },
        {
          path: appRoutes.VEHICLE_DETAILS_INOVICE,
          element: <vehicleDetailsInvoice />,
        },
        {
          path: appRoutes.RENEW_POLICY,
          element: <RenewPolicyPage />,
        },
        {
          path: appRoutes.RENEW_POLICY_PAYMENT_RESULT,
          element: <RenewPolicyPaymentResult />,
        },
        { path: "self-policy-punch", element: <SelfPolicyPunch /> },
        { path: "fresh-policy-punch", element: <FreshPolicyPunch /> },
        {
          path: "fresh-policy-punch/payment/result",
          element: <FreshPolicyPunchResult />,
        },
      ],
    },
    {
      path: "",
      element: <PrivateRoutes />,
      children: [{ path: appRoutes.ROLE, element: <SelectUserRole /> }],
    },
    {
      path: "",
      element: <PrivateRoutes />,
      children: [
        {
          path: appRoutes.SALE_CATEGORY,
          element: <App Component={<SaleCategory />} />,
        },
        {
          path: "dashboard/default",
          element: <App Component={<Default />} />,
        },
        {
          path: "dashboard/default/",
          element: <App Component={<RolesSelection />} />,
        },
        {
          path: "dashboard/ecommerce/:layout/",
          element: <App Component={<Ecommerce />} />,
        },
        {
          path: "widgets/general/:layout",
          element: <App Component={<GeneralWidget />} />,
        },
        {
          path: "widgets/chart/:layout",
          element: <App Component={<ChartsWidget />} />,
        },
        {
          path: "ui-kits/statecolor/:layout",
          element: <App Component={<StateColor />} />,
        },
        {
          path: "ui-kits/typography/:layout",
          element: <App Component={<Typography />} />,
        },
        {
          path: "ui-kits/avatar/:layout",
          element: <App Component={<Avatars />} />,
        },
        {
          path: "ui-kits/helperclass/:layout",
          element: <App Component={<HelperClasses />} />,
        },
        { path: "ui-kits/grid/:layout", element: <App Component={<Grid />} /> },
        {
          path: "ui-kits/tagsandpills/:layout",
          element: <App Component={<TagAndPills />} />,
        },
        {
          path: "ui-kits/progress-bar/:layout",
          element: <App Component={<Progress />} />,
        },
        {
          path: "ui-kits/modal/:layout",
          element: <App Component={<Modal />} />,
        },
        {
          path: "ui-kits/alert/:layout",
          element: <App Component={<Alert />} />,
        },
        {
          path: "ui-kits/popover/:layout",
          element: <App Component={<Popover />} />,
        },
        {
          path: "ui-kits/tooltips/:layout",
          element: <App Component={<Tooltip />} />,
        },
        {
          path: "ui-kits/spinner/:layout",
          element: <App Component={<Spinner />} />,
        },
        {
          path: "ui-kits/dropdown/:layout",
          element: <App Component={<Dropdown />} />,
        },
        {
          path: "ui-kits/tab-bootstrap/:layout",
          element: <App Component={<TabBootstrap />} />,
        },
        {
          path: "ui-kits/tab-line/:layout",
          element: <App Component={<TabLine />} />,
        },
        {
          path: "ui-kits/accordion/:layout",
          element: <App Component={<Accordian />} />,
        },
        {
          path: "ui-kits/shadow/:layout",
          element: <App Component={<Shadow />} />,
        },
        { path: "ui-kits/list/:layout", element: <App Component={<List />} /> },
        {
          path: "bonus-ui/scrollable/:layout",
          element: <App Component={<Scrolling />} />,
        },
        {
          path: "bonus-ui/bootstrap-notify/:layout",
          element: <App Component={<BootstrapNotify />} />,
        },
        {
          path: "bonus-ui/rating/:layout",
          element: <App Component={<Rating />} />,
        },
        {
          path: "bonus-ui/dropzone/:layout",
          element: <App Component={<Dropzone />} />,
        },
        {
          path: "bonus-ui/sweetAlert/:layout",
          element: <App Component={<SweetAlert />} />,
        },
        {
          path: "bonus-ui/tourComponent/:layout",
          element: <App Component={<Tour />} />,
        },
        {
          path: "bonus-ui/stickyNotes/:layout",
          element: <App Component={<Sticky />} />,
        },
        {
          path: "bonus-ui/breadcrumb/:layout",
          element: <App Component={<Breadcrumb />} />,
        },
        {
          path: "bonus-ui/imageCropper/:layout",
          element: <App Component={<Imagecrop />} />,
        },
        {
          path: "bonus-ui/rangeSlider/:layout",
          element: <App Component={<RangeSlider />} />,
        },
        {
          path: "bonus-ui/carousel/:layout",
          element: <App Component={<Carousel />} />,
        },
        {
          path: "bonus-ui/ribbons/:layout",
          element: <App Component={<Ribbons />} />,
        },
        {
          path: "bonus-ui/pagination/:layout",
          element: <App Component={<Pagination />} />,
        },
        {
          path: "bonus-ui/dragNDropComp/:layout",
          element: <App Component={<DragAndDrop />} />,
        },
        {
          path: "bonus-ui/image-upload/:layout",
          element: <App Component={<UploadImage />} />,
        },
        {
          path: "bonus-ui/card/basicCards/:layout",
          element: <App Component={<BasicCards />} />,
        },
        {
          path: "bonus-ui/card/creativeCards/:layout",
          element: <App Component={<ThemeCards />} />,
        },
        {
          path: "bonus-ui/card/tabCard/:layout",
          element: <App Component={<TabCard />} />,
        },
        {
          path: "bonus-ui/card/draggingCards/:layout",
          element: <App Component={<DraggingCards />} />,
        },

        {
          path: "bonus-ui/timelines/timeline1/:layout",
          element: <App Component={<Timeline1 />} />,
        },

        {
          path: "icons/flagIcons/:layout",
          element: <App Component={<FlagIcons />} />,
        },
        {
          path: "icons/fontAwsomeIcon/:layout",
          element: <App Component={<FontAwsomeIcon />} />,
        },
        {
          path: "icons/icoIcons/:layout",
          element: <App Component={<IcoIcons />} />,
        },
        {
          path: "icons/featherIcons/:layout",
          element: <App Component={<FeatherIcons />} />,
        },
        {
          path: "icons/themifyIcons/:layout",
          element: <App Component={<ThemifyIcons />} />,
        },
        {
          path: "icons/weatherIcons/:layout",
          element: <App Component={<WeatherIcons />} />,
        },
        {
          path: "buttons/default-btn/:layout",
          element: <App Component={<DefaultButton />} />,
        },
        {
          path: "buttons/flatBtn/:layout",
          element: <App Component={<FlatButton />} />,
        },
        {
          path: "buttons/edgeBtn/:layout",
          element: <App Component={<EdgeButton />} />,
        },
        {
          path: "buttons/raisedBtn/:layout",
          element: <App Component={<RaisedButton />} />,
        },
        {
          path: "buttons/groupBtn/:layout",
          element: <App Component={<GroupButton />} />,
        },
        {
          path: "forms/form-validation/:layout",
          element: <App Component={<FormValidation />} />,
        },
        {
          path: "affiliate/client/companies/test",
          element: <App Component={<AffiliationCompaniesTest />} />,
        },
        {
          path: "forms/baseInput/:layout",
          element: <App Component={<BaseInput />} />,
        },
        {
          path: "forms/inputGroup/:layout",
          element: <App Component={<InputGroup />} />,
        },
        {
          path: "forms/megaOptions/:layout",
          element: <App Component={<MegaOption />} />,
        },
        {
          path: "forms/radio-checkbox/:layout",
          element: <App Component={<CheckboxandRadio />} />,
        },
        {
          path: "form-layout/formDefault/:layout",
          element: <App Component={<FormDefault />} />,
        },
        {
          path: "form-layout/formWizard/:layout",
          element: <App Component={<FormWizard1 />} />,
        },

        {
          path: "form-widget/datepicker",
          element: <App Component={<Datepicker />} />,
        },
        {
          path: "form-widget/timepicker/:layout",
          element: <App Component={<Timepicker />} />,
        },
        {
          path: "form-widget/typeahead/:layout",
          element: <App Component={<Typeahead />} />,
        },

        {
          path: "table/basic/:layout",
          element: <App Component={<BasicTabels />} />,
        },
        {
          path: "table/border/:layout",
          element: <App Component={<BorderTable />} />,
        },
        {
          path: "table/sizing/:layout",
          element: <App Component={<SizingTable />} />,
        },
        {
          path: "table/styling/:layout",
          element: <App Component={<StylingTable />} />,
        },
        {
          path: "table/datatable/:layout",
          element: <App Component={<DataTable />} />,
        },

        {
          path: "charts/apexCharts/:layout",
          element: <App Component={<Apexcharts />} />,
        },
        {
          path: "charts/googleChart/:layout",
          element: <App Component={<GoogleCharts />} />,
        },
        {
          path: "charts/knobChart/:layout",
          element: <App Component={<KnobChart />} />,
        },
        {
          path: "charts/chartistComponent/:layout",
          element: <App Component={<Chartist />} />,
        },

        {
          path: "app/gallery/imageGallery/:layout",
          element: <App Component={<ImageGallery />} />,
        },
        {
          path: "app/gallery/imageWithDesc/:layout",
          element: <App Component={<ImageWithDesc />} />,
        },
        {
          path: "app/gallery/mesonryGallery/:layout",
          element: <App Component={<MesonryGallery />} />,
        },
        {
          path: "app/gallery/mesonryDesc/:layout",
          element: <App Component={<MesonryDesc />} />,
        },
        {
          path: "app/gallery/imageHover/:layout",
          element: <App Component={<ImageHover />} />,
        },

        {
          path: "app/blog/blogDetail/:layout",
          element: <App Component={<BlogDetail />} />,
        },
        {
          path: "app/blog/blogSingle/:layout",
          element: <App Component={<BlogSingle />} />,
        },
        {
          path: "app/blog/blogPost/:layout",
          element: <App Component={<BlogPost />} />,
        },

        {
          path: "app/jobSearch/cardView/:layout",
          element: <App Component={<CardView />} />,
        },
        {
          path: "app/jobSearch/job-list/:layout",
          element: <App Component={<JobList />} />,
        },
        {
          path: "app/jobSearch/job-detail/:layout",
          element: <App Component={<JobDetail />} />,
        },
        {
          path: "app/jobSearch/job-apply/:layout",
          element: <App Component={<JobApply />} />,
        },

        {
          path: "app/learning/learning-list/:layout",
          element: <App Component={<Learninglist />} />,
        },
        {
          path: "app/learning/learning-detail/:layout",
          element: <App Component={<LearningDeatil />} />,
        },

        {
          path: "app/users/userProfile/:layout",
          element: <App Component={<UserProfile />} />,
        },
        {
          path: "app/users/userEdit/:layout",
          element: <App Component={<UserEdit />} />,
        },
        {
          path: "app/users/userCards/:layout",
          element: <App Component={<UserCards />} />,
        },
        {
          path: "app/calendar/basic-calendar/:layout",
          element: <App Component={<BasicCalender />} />,
        },
        {
          path: "app/calendar/draggable-calendar/:layout",
          element: <App Component={<DraggableCalendar />} />,
        },

        {
          path: "app/map/googleMap/:layout",
          element: <App Component={<GoogleMap />} />,
        },

        {
          path: "app/editor/ckEditor/:layout",
          element: <App Component={<CkEditor />} />,
        },
        {
          path: "app/editor/mdeEditor/:layout",
          element: <App Component={<MdeEditor />} />,
        },

        {
          path: "app/social-app/:layout",
          element: <App Component={<SocialApp />} />,
        },
        {
          path: "app/faq/:layout",
          element: <App Component={<FaqComponent />} />,
        },
        {
          path: "app/knowledgebase/:layout",
          element: <App Component={<KnowledgebaseComponent />} />,
        },
        {
          path: "app/support-ticket/:layout",
          element: <App Component={<SupportTicket />} />,
        },

        {
          path: "pages/samplepage/:layout",
          element: <App Component={<Sample />} />,
        },
        {
          path: "playground",
          element: <App Component={<Playground />} />,
        },
        {
          path: "mis_cron",
          element: <App Component={<MisReports />} />,
        },
        {
          path: "pages/searchpage/:layout",
          element: <App Component={<Search />} />,
        },

        {
          path: "app/ecommerce/product/:layout",
          element: <App Component={<Product />} />,
        },
        {
          path: "app/ecommerce/product-page/:id/:layout",
          element: <App Component={<ProductDetail />} />,
        },
        {
          path: "app/ecommerce/cart/:layout",
          element: <App Component={<Cart />} />,
        },
        {
          path: "app/ecommerce/wishlist/:layout",
          element: <App Component={<Wishlist />} />,
        },
        {
          path: "app/ecommerce/checkout/:layout",
          element: <App Component={<Checkout />} />,
        },
        {
          path: "app/ecommerce/invoice/:layout",
          element: <App Component={<Invoice />} />,
        },
        {
          path: "app/ecommerce/product-list/:layout",
          element: <App Component={<Productlist />} />,
        },
        {
          path: "app/ecommerce/payment-details/:layout",
          element: <App Component={<Paymentdetails />} />,
        },
        {
          path: "app/ecommerce/orderhistory/:layout",
          element: <App Component={<OrderHistory />} />,
        },
        {
          path: "app/ecommerce/pricing/:layout",
          element: <App Component={<Pricing />} />,
        },

        {
          path: "app/email-app/:layout",
          element: <App Component={<Email />} />,
        },
        { path: "app/chat-app/:layout", element: <App Component={<Chat />} /> },
        {
          path: "app/bookmark/:layout",
          element: <App Component={<Bookmark />} />,
        },
        { path: "app/task/:layout", element: <App Component={<TaskApp />} /> },

        {
          path: "app/project/project-list/:layout",
          element: <App Component={<ProjectList />} />,
        },
        {
          path: "app/project/new-project/:layout",
          element: <App Component={<NewProject />} />,
        },

        {
          path: "app/file-manager/:layout",
          element: <App Component={<FileManager />} />,
        },
        {
          path: "app/kanban-board/:layout",
          element: <App Component={<kanbanBoard />} />,
        },

        {
          path: "app/contact/:layout",
          element: <App Component={<ContactApp />} />,
        },

        {
          path: "app/todo-app/todo/:layout",
          element: <App Component={<Todo />} />,
        },
        {
          path: "app/todo-app/todo-firebase/:layout",
          element: <App Component={<TodoFirebase />} />,
        },
        {
          path: "allocation/hierarchy",
          element: <App Component={<Hierarchy />} />,
        },

        {
          path: "allocation/dealers-details",
          element: <App Component={<Allocation />} />,
        },
        {
          path: "wallet/statement",
          element: <App Component={<UserWalletStatement />} />,
        },
        {
          path: appRoutes.SALE_CATEGORY,
          element: <App Component={<SaleCategory />} />,
        },
        { path: "sales/products", element: <App Component={<Products />} /> },
        {
          path: "sales/modelproducts",
          element: <App Component={<ModelProducts />} />,
        },
        { path: "sales/services", element: <App Component={<Services />} /> },
        { path: "sales/coupons", element: <App Component={<Coupons />} /> },
        { path: "sales/policies", element: <App Component={<Policies />} /> },
        {
          path: "sales/policies/certificate",
          element: <App Component={<Certificate />} />,
        },
        {
          path: "sales/policies/invoice",
          element: <App Component={<CommissionInvoice />} />,
        },
        {
          path: "sales/policies/dealership-claim",
          element: checkRoleAccess(
            ["DEALERSHIP_MANAGER", "ADMIN", "TEAM_MEMBER", "SUPER_ADMIN"],
            <App Component={<DealershipClaim />} />
          ),
        },
        {
          path: "sales/policies/certificate2",
          element: <App Component={<Certificate2 />} />,
        },
        {
          path: "sales/certificates/search-certificate",
          element: <App Component={<SearchCertificates />} />,
        },
        {
          path: "affiliations/wallet/search_payu",
          element: <App Component={<SearchPayUEntries />} />,
        },
        {
          path: "sales/policies/customer",
          element: <App Component={<Customers />} />,
        },
        {
          path: "affiliations/brands",
          element: <App Component={<Brands />} />,
        },
        {
          path: "affiliations/models",
          element: <App Component={<Models />} />,
        },
        {
          path: "affiliations/variants",
          element: <App Component={<Variants />} />,
        },
        {
          path: "affiliations/fuels",
          element: <App Component={<Fuels />} />,
        },
        {
          path: "affiliations/transmissions",
          element: <App Component={<Transmissions />} />,
        },
        {
          path: "affiliations/zone-master",
          element: <App Component={<ZoneMaster />} />,
        },
        {
          path: "affiliations/region-master",
          element: <App Component={<RegionMaster />} />,
        },
        {
          path: "affiliations/companies",
          element: <App Component={<Companies />} />,
        },
        {
          path: "affiliations/globalSync",
          element: <App Component={<GlobalSync />} />,
        },
        {
          path: "affiliate/client/companies/add-company",
          element: <App Component={<CompaniesBasicDetails />} />,
        },
        // {
        //   path: "affiliate/client/companies/edit-company",
        //   element: <App Component={<CompaniesEditBasicDetails />} />,
        // },
        {
          path: "affiliate/client/companies/edit-company/:company_id",
          element: <App Component={<CompaniesEditBasicDetails />} />,
        },
        {
          path: "affiliate/client/companies/configuration",
          element: <App Component={<ClientConfiguration />} />,
        },
        {
          path: "affiliate/client/companies/configuration/:company_id",
          element: <App Component={<ClientConfiguration />} />,
        },
        {
          path: "affiliate/client/companies/assigned-products/:company_id",
          element: <App Component={<AssignedProductsToClient />} />,
        },
        {
          path: "affiliations/dealerships",
          element: <App Component={<Dealerships />} />,
        },
        {
          path: "mis_dealers/dealerships",
          element: <App Component={<DealershipsForMisRole />} />,
        },
        {
          path: "affiliations/dealerCodeMapping",
          element: <App Component={<DealerCodeMapping />} />,
        },
        {
          path: "affiliate/client/dealerships/assigned-dealerships/:company_id",
          element: <App Component={<AssignedDealershipsToClient />} />,
        },
        {
          path: "navision/admin_wallet",
          element: <App Component={<AdminWallet />} />,
        },
        {
          path: "navision/admin_wallet_closure",
          element: <App Component={<AdminWalletReport />} />,
        },
        {
          path: "navision/wallet_refund",
          element: <App Component={<WalletRefund />} />,
        },
        {
          path: "navision/wallet_map",
          element: <App Component={<WalletMap />} />,
        },
        {
          path: "users/users",
          element: <App Component={<Users dataType={1} />} />,
        },
        {
          path: "pages/page-coming-soon",
          element: <App Component={<PageComingSoon />} />,
        },
        {
          path: "roles",
          element: <App Component={<Roles />} />,
        },
        {
          path: "permissions",
          element: <App Component={<Permissions />} />,
        },
        {
          path: "role/permissions",
          element: <App Component={<RolePermissions />} />,
        },
        {
          path: "user/roles",
          element: <App Component={<UserRoles />} />,
        },
        {
          path: "commissions/static",
          element: <App Component={<StaticCommission />} />,
        },
        {
          path: "commissions/incentivized",
          element: <App Component={<IncentivizedCommission />} />,
        },
        { path: "commissions/status", element: <App Component={<Status />} /> },
        { path: "reports/sales", element: <App Component={<SalesReport />} /> },
        {
          path: "reports/policy",
          element: <App Component={<PolicyReport />} />,
        },
        {
          path: "mis_reports/policy",
          element: <App Component={<PolicyReportForMisRole />} />,
        },
        { path: "reports/mis", element: <App Component={<MISReport />} /> },
        {
          path: "mis_reports/mis",
          element: <App Component={<MISReportForMSIRole />} />,
        },
        {
          path: "reports/dealermis",
          element: <App Component={<DealerMISReport />} />,
        },
        {
          path: "reports/rsa-sales",
          element: <App Component={<RsaSalesReport />} />,
        },
        {
          path: "reports/target-allotment",
          element: <App Component={<TargetAllotment />} />,
        },
        {
          path: "reports/target-vs-achievement",
          element: <App Component={<TargetVsAchievement />} />,
        },
        {
          path: "targetAllotment/TargetAlloment",
          element: <App Component={<TargetAlloment />} />,
        },
        {
          path: "reports/dealer_kyc_reports",
          element: <App Component={<DealerKYC />} />,
        },
        {
          path: "reports/admin/wallet",
          element: <App Component={<AdminWalletHistory />} />,
        },
        {
          path: "reports/admin/ea_wallet",
          element: <App Component={<EAWalletHistory />} />,
        },
        {
          path: "reports/user/wallet",
          element: <App Component={<UserWalletReport />} />,
        },

        {
          path: "reports/wallet-refund-list",
          element: <App Component={<WalletRefundList />} />,
        },

        {
          path: "logs/server/access",
          element: <App Component={<AccessLogs />} />,
        },
        {
          path: "logs/policy",
          element: <App Component={<PolicyElastic />} />,
        },
        {
          path: "sales/policy",
          element: <App Component={<PolicyElastic />} />,
        },
        {
          path: "logs/server/error",
          element: <App Component={<ErrorLogs />} />,
        },
        {
          path: "logs/server/cron",
          element: <App Component={<CronLogs />} />,
        },
        { path: "logs/api", element: <App Component={<APILogs />} /> },
        {
          path: "logs/activity",
          element: <App Component={<ActivityLogs />} />,
        },
        {
          path: "affiliations/dsp",
          element: <App Component={<DSP />} />,
        },
        {
          path: "account/profile/edit",
          element: <App Component={<MyAccount />} />,
        },
        {
          path: "dealerCommission/invoice-status",
          element: <App Component={<CommissionInvoiceStatus />} />,
        },
        {
          path: "dealerCommission/dealer-invoice-status",
          element: <App Component={<DealerCommissionInvoiceStatus />} />,
        },
        {
          path: "account/password/edit",
          element: <App Component={<ChangePassword />} />,
        },
        {
          path: "sales/products/assign",
          element: <App Component={<ProductsAssign />} />,
        },
        // {
        //   path: "sales/policies/create/:sales_category_id",
        //   // element: <App Component={<PolicyCreate />} />, // one page policy punch
        //   element: <App Component={<PolicyCreateWithStepper />} />, // stepper policy punch
        // },
        {
          path: "sales/one-page-policies/create/:sales_category_id",
          element: <App Component={<PolicyCreate />} />, // one page policy punch
        },
        {
          path: "sales/policies/create/:sales_category_id",
          element: <App Component={<PolicyCreateWithStepper />} />, // stepper policy punch
        },
        {
          path: "sales/policies/bulk",
          element: <App Component={<BulkPolicyUpload />} />,
        },
        {
          path: "reports/edit-target-allotment/:company_id",
          element: <App Component={<EditTargetAllotment />} />,
        },
        {
          path: "affiliations/companies/users",
          // element: <App Component={<CompanyUsers />} />,
          element: <App Component={<Users dataType={2} />} />,
        },
        {
          path: "affiliations/dealerships/users",
          // element: <App Component={<DealershipUsers />} />,
          element: <App Component={<Users dataType={3} />} />,
        },
        {
          path: "affiliations/dealerships/bulkupload",
          element: <App Component={<BulkUploadDealership />} />,
        },
        {
          path: "documents/helpdesk",
          element: <App Component={<Helpdesk />} />,
        },
        { path: "documents/kyc", element: <App Component={<KYC />} /> },
        {
          path: "documents/kycBackup",
          element: <App Component={<KYCBackup />} />,
        },
        {
          path: "documents/viewhelpdesk",
          element: <App Component={<Viewhelpdesk />} />,
        },
        {
          path: "e-learning/courses",
          element: <App Component={<ELearningCourses />} />,
        },
        {
          path: "e-learning/sections",
          element: <App Component={<ELearningCourseSections />} />,
        },
        {
          path: "e-learning/courses_module",
          element: <App Component={<ECourseModule />} />,
        },
        {
          path: "e-learning/courses_topic",
          element: <App Component={<ECourseModuleTopic />} />,
        },
        {
          path: "e-learning/courses_quiz",
          element: <App Component={<ECourseModuleQuiz />} />,
        },
        {
          path: "e-learning/play",
          element: <App Component={<ELearningPlay />} />,
        },
        {
          path: "e-learning/quiz",
          element: <App Component={<ELearningQuiz />} />,
        },
        {
          path: "manage/e-learning/course",
          element: <App Component={<ManageCourse />} />,
        },
        {
          path: "manage/e-learning/course/sections",
          element: <App Component={<ManageCourseSections />} />,
        },
        {
          path: "requests/my-requests",
          element: <App Component={<MyRequests />} />,
        },
        {
          path: "requests/admin-requests",
          element: <App Component={<AdminRequests />} />,
        },
        {
          path: "requests/requests-for-me",
          element: <App Component={<RequestsForMe type="my-requests" />} />,
        },
        {
          path: "requests/requests-for-me-visit",
          element: <App Component={<RequestsForMeByRole type="my-requests" />} />,
        },
        {
          path: "requests/my-requests-visit",
          element: <App Component={<MyRequestsVisit />} />,
        },
        {
          path: "requests/my-actions",
          element: <App Component={<RequestsForMe type="my-actions" />} />,
        },
        {
          path: "requests/upload-image-to-s3",
          element: <App Component={<UploadImageToS3 />} />,
        },
        {
          path: "wallet/credits",
          element: checkRoleAccess(
            ["DEALERSHIP_MANAGER"],
            <App Component={<WalletCredits />} />
          ),
        },
        {
          path: "pages/auth/loginWithBgImg1",
          element: <App Component={<LoginWithBgImage />} />,
        },
        {
          path: "affiliations/dms",
          element: <App Component={<DMSData />} />,
        },
        {
          path: "affiliations/festive_greetings",
          element: <App Component={<FestiveGreetings />} />,
        },
        {
          path: "affiliations/dmsList",
          element: <App Component={<DMSList />} />,
        },
        // {
        //   path: "wallet/statement",
        //   element: <App Component={<UserWalletStatement />} />,
        // },
        {
          path: "sales/taxes/countries",
          element: <App Component={<Countries />} />,
        },
        { path: "sales/taxes/states", element: <App Component={<States />} /> },
        {
          path: "sales/taxes/parent-tax",
          element: <App Component={<ParentTaxes />} />,
        },
        {
          path: "sales/taxes/sub-taxes",
          element: <App Component={<SubTaxes />} />,
        },
        {
          path: "reports/bdm-sales",
          element: <App Component={<BdmSalesReport />} />,
        },
        {
          path: "reports/BDM-Data",
          element: <App Component={<BdmTable />} />,
        },
        {
          path: "sales/bdm-policies",
          element: <App Component={<BdmPolicies />} />,
        },
        {
          path: "sales/products/productList",
          element: <App Component={<ProductLists />} />,
        },
        {
          path: "users/add",
          element: <App Component={<AddUser />} />,
        },
        {
          path: "access-permission",
          element: <App Component={<Permission />} />,
        },
        {
          path: "online-policy-punch-success",
          element: <App Component={<PolicySuccess />} />,
        },
        {
          path: "online-policy-punch-failure",
          element: <App Component={<PolicyFailure />} />,
        },
        {
          path: "payment-success",
          element: <App Component={<PaymentSuccess />} />,
        },
        {
          path: "payment-failure",
          element: <App Component={<PaymentFailure />} />,
        },
        {
          path: "session",
          element: <App Component={<Session />} />,
        },
        {
          path: "getOtp",
          element: <App Component={<GetOtp />} />,
        },
        {
          path: "birthday_logs",
          element: <App Component={<BirthdayLogs />} />,
        },
        {
          path: "automated_email",
          element: <App Component={<AutomatedEmail />} />,
        },
        {
          path: "reports/visiting_reports",
          element: <App Component={<VisitingReport />} />,
        },
        {
          path: "reports/IndiaMap",
          element: <App Component={<IndiaMap />} />,
        },
        {
          path: "reports/globalSync",
          element: <App Component={<globalSync />} />,
        },
        {
          path: "affiliations/master_data_management",
          element: <App Component={<MasterDataManagement />} />,
        },
        {
          path: "affiliations/leads",
          element: <App Component={<LeadsModule />} />,
        },
        {
          path: "affiliations/meetings",
          element: <App Component={<MeetingsModule />} />,
        },
        {
          path: "target-allotment-client",
          element: <App Component={<TargetAllotmentClient />} />,
        },
        {
          path: "target-allotment-client/:company_id",
          element: <App Component={<SetTarget />} />,
        },
        {
          path: "monthly-target-destribution",
          element: <App Component={<MonthlyTargetDestribution />} />,
        },
        {
          path: "report/incentive-report",
          element: <App Component={<IncentiveReport />} />,
        },
        {
          path: "report/incentive",
          element: <App Component={<ZMIncentiveReport />} />,
        },
        {
          path: "sales/inclusion-policies/bulk",
          element: <App Component={<BulkPolicyUploadHero />} />,
        },
        {
          path: "dealer-qr-code",
          element: (
            <DealerQRCodeRouteGuard>
              <App Component={<DealerQRCode />} />
            </DealerQRCodeRouteGuard>
          ),
        },
      ],
    },
    { path: appRoutes.NOT_FOUND, element: <Error400 /> },
  ]);
}

const App = Loadable(lazy(() => import("../components/app")));
const MasterDataManagement = Loadable(
  lazy(() => import("../components/salesFunnel/mastersModule/MasterDataManagement.jsx"))
);
const LeadsModule = Loadable(
  lazy(() => import("../components/salesFunnel/leadsModule/leadsModule"))
);
const MeetingsModule = Loadable(
  lazy(() => import("../components/salesFunnel/meetingsModule/meetingsModule"))
);
const Permission = Loadable(
  lazy(() => import("../components/permission_management/Permissions"))
);
const PolicySuccess = Loadable(
  lazy(() => import("../components/sales/policies/PolicySuccess"))
);
const PolicyFailure = Loadable(
  lazy(() => import("../components/sales/policies/PolicyFailure"))
);
const PaymentSuccess = Loadable(
  lazy(() => import("../components/payment/PaymentSuccess"))
);
const PaymentFailure = Loadable(
  lazy(() => import("../components/payment/PaymentFailure"))
);
const Signin = Loadable(lazy(() => import("../auth/signin")));
const RememberMe = Loadable(lazy(() => import("../auth/rememberMe")));
const Login = Loadable(lazy(() => import("../pages/authentication/login")));
const CallBackLogin = Loadable(
  lazy(() => import("../pages/authentication/CallBackupUrl"))
);
const LoginWithBgImage = Loadable(
  lazy(() => import("../pages/authentication/loginWithBgImage"))
);
const LoginWithBgVideo = Loadable(
  lazy(() => import("../pages/authentication/loginWithBgVideo"))
);
const LoginWithValidation = Loadable(
  lazy(() => import("../pages/authentication/loginwithValidation"))
);
const Register = Loadable(
  lazy(() => import("../pages/authentication/register"))
);
const RegisterWithBgImage = Loadable(
  lazy(() => import("../pages/authentication/registerWithBgImage"))
);
const RegisterWithBgVideo = Loadable(
  lazy(() => import("../pages/authentication/registerWithBgVideo"))
);
const UnlockUser = Loadable(
  lazy(() => import("../pages/authentication/unlockUser"))
);
const Forgetpwd = Loadable(
  lazy(() => import("../pages/authentication/forgetpwd"))
);
const Resetpwd = Loadable(
  lazy(() => import("../pages/authentication/resetpwd"))
);
const Error400 = Loadable(lazy(() => import("../pages/errors/error400")));
const Error401 = Loadable(lazy(() => import("../pages/errors/error401")));
const Error403 = Loadable(lazy(() => import("../pages/errors/error403")));
const Error404 = Loadable(lazy(() => import("../pages/errors/error404")));
const Error500 = Loadable(lazy(() => import("../pages/errors/error500")));
const Error503 = Loadable(lazy(() => import("../pages/errors/error503")));
const Comingsoon = Loadable(
  lazy(() => import("../pages/comingSoon/comingsoon"))
);
const ComingsoonImg = Loadable(
  lazy(() => import("../pages/comingSoon/comingsoonImg"))
);
const ComingsoonVideo = Loadable(
  lazy(() => import("../pages/comingSoon/comingsoonVideo"))
);
const Maintenance = Loadable(lazy(() => import("../pages/maintenance")));
const Callback = Loadable(lazy(() => import("../auth/callback")));
const ForgotPassword = Loadable(lazy(() => import("../auth/forgotPassword")));
const ValidateOTP = Loadable(lazy(() => import("../auth/validateOTP")));
const MailSent = Loadable(lazy(() => import("../auth/mail-sent")));
const ResetPassword = Loadable(lazy(() => import("../auth/resetPassword")));
const SelectUserRole = Loadable(lazy(() => import("../auth/selectUserRole")));
const FirstLoginValidateOTP = Loadable(
  lazy(() => import("../auth/firstLoginValidateOTP"))
);
const Allocation = Loadable(
  lazy(() => import("../components/allocation/Allocation"))
);
const Hierarchy = Loadable(
  lazy(() => import("../components/hierarchy/Hierarchy"))
);
const TargetAlloment = Loadable(
  lazy(() => import("../components/targetAllotment/TargetAlloment"))
);
const TargetPage = Loadable(
  lazy(() => import("../components/targetAllotment/TargetPage"))
);
const TeamMemberDashboard = Loadable(
  lazy(() => import("../components/dashboard/TeamMemberDashboard"))
);
const DealerDashboard = Loadable(
  lazy(() => import("../components/dashboard/DealerDashboard"))
);
const CcmDashboard = Loadable(
  lazy(() => import("../components/dashboard/CcmDashboard"))
);
const DspDashboard = Loadable(
  lazy(() => import("../components/dashboard/DspDashboard"))
);
const FinanceDasbaord = Loadable(
  lazy(() => import("../components/dashboard/FinanceDasbaord"))
);
const Home = Loadable(lazy(() => import("../components/dashboard/Home")));
const vehicleDetails = Loadable(
  lazy(() => import("../components/dashboard/vehicleDetais"))
);
const vehicleDetailsInvoice = Loadable(
  lazy(() => import("../components/dashboard/vehicleDetailsInovie"))
);
const RenewPolicyPage = Loadable(
  lazy(() => import("../components/renew-policy/RenewPolicyPage.jsx"))
);
const RenewPolicyPaymentResult = Loadable(
  lazy(() => import("../components/renew-policy/RenewPolicyPaymentResult.jsx"))
);

//Protected Dashboard routes
// const SaleCategory = Loadable(lazy(() => import('../components/sales/categories/categories')))
// Dashboard
const Default = Loadable(lazy(() => import("../components/dashboard/default")));
const RolesSelection = Loadable(lazy(() => import("../auth/RolesSelection")));
const Ecommerce = Loadable(
  lazy(() => import("../components/dashboard/ecommerce"))
);

// Widgets
const GeneralWidget = Loadable(
  lazy(() => import("../components/widgets/general"))
);
const ChartsWidget = Loadable(
  lazy(() => import("../components/widgets/charts"))
);

// UI Kits
const StateColor = Loadable(
  lazy(() => import("../components/ui-kits/statecolor"))
);
const Typography = Loadable(
  lazy(() => import("../components/ui-kits/typography"))
);
const Avatars = Loadable(lazy(() => import("../components/ui-kits/avatars")));
const HelperClasses = Loadable(
  lazy(() => import("../components/ui-kits/helperClasses"))
);
const Grid = Loadable(lazy(() => import("../components/ui-kits/grid")));
const TagAndPills = Loadable(
  lazy(() => import("../components/ui-kits/tagAndPills"))
);
const Progress = Loadable(lazy(() => import("../components/ui-kits/progress")));
const Modal = Loadable(lazy(() => import("../components/ui-kits/modal")));
const Alert = Loadable(lazy(() => import("../components/ui-kits/alert")));
const Popover = Loadable(lazy(() => import("../components/ui-kits/popover")));
const Tooltip = Loadable(lazy(() => import("../components/ui-kits/tooltip")));
const Spinner = Loadable(lazy(() => import("../components/ui-kits/spinner")));
const Dropdown = Loadable(lazy(() => import("../components/ui-kits/dropDown")));
const TabBootstrap = Loadable(
  lazy(() => import("../components/ui-kits/tabs/tabBootstrap"))
);
const TabLine = Loadable(
  lazy(() => import("../components/ui-kits/tabs/tabLine"))
);
const Accordian = Loadable(
  lazy(() => import("../components/ui-kits/accordian"))
);
const Shadow = Loadable(lazy(() => import("../components/ui-kits/shadow")));
const List = Loadable(lazy(() => import("../components/ui-kits/list")));

// Bonus UI
const Scrolling = Loadable(
  lazy(() => import("../components/bonus_ui/scrolling"))
);
const BootstrapNotify = Loadable(
  lazy(() => import("../components/bonus_ui/bootstrapNotify"))
);
const Rating = Loadable(lazy(() => import("../components/bonus_ui/rating")));
const Dropzone = Loadable(
  lazy(() => import("../components/bonus_ui/dropzone"))
);
const SweetAlert = Loadable(
  lazy(() => import("../components/bonus_ui/sweetAlert"))
);
const Tour = Loadable(lazy(() => import("../components/bonus_ui/tour")));
const Sticky = Loadable(lazy(() => import("../components/bonus_ui/sticky")));
const Breadcrumb = Loadable(
  lazy(() => import("../components/bonus_ui/breadcrumb"))
);
const Imagecrop = Loadable(
  lazy(() => import("../components/bonus_ui/imagecrop"))
);
const RangeSlider = Loadable(
  lazy(() => import("../components/bonus_ui/rangeSlider"))
);
const Carousel = Loadable(
  lazy(() => import("../components/bonus_ui/carousel"))
);
const Ribbons = Loadable(lazy(() => import("../components/bonus_ui/ribbons")));
const Pagination = Loadable(
  lazy(() => import("../components/bonus_ui/pagination"))
);
const DragAndDrop = Loadable(
  lazy(() => import("../components/bonus_ui/dragAndDrop"))
);
const UploadImage = Loadable(
  lazy(() => import("../components/bonus_ui/uploadImage"))
);

// Cards
const BasicCards = Loadable(
  lazy(() => import("../components/bonus_ui/cards/basicCards"))
);
const ThemeCards = Loadable(
  lazy(() => import("../components/bonus_ui/cards/themeCards"))
);
const TabCard = Loadable(
  lazy(() => import("../components/bonus_ui/cards/tabCard"))
);
const DraggingCards = Loadable(
  lazy(() => import("../components/bonus_ui/cards/draggingCards"))
);

// Timeline
const Timeline1 = Loadable(
  lazy(() => import("../components/bonus_ui/timelines/timeline"))
);

// Icons
const FlagIcons = Loadable(lazy(() => import("../components/icons/flagIcons")));
const FontAwsomeIcon = Loadable(
  lazy(() => import("../components/icons/fontAwsomeIcon"))
);
const IcoIcons = Loadable(lazy(() => import("../components/icons/icoIcons")));
const ThemifyIcons = Loadable(
  lazy(() => import("../components/icons/themifyIcons"))
);
const FeatherIcons = Loadable(
  lazy(() => import("../components/icons/featherIcons"))
);
const WeatherIcons = Loadable(
  lazy(() => import("../components/icons/weatherIcons"))
);

// Buttons
const DefaultButton = Loadable(
  lazy(() => import("../components/buttons/default-Button"))
);
const EdgeButton = Loadable(
  lazy(() => import("../components/buttons/edgeButton"))
);
const FlatButton = Loadable(
  lazy(() => import("../components/buttons/flatButton"))
);
const GroupButton = Loadable(
  lazy(() => import("../components/buttons/groupButton"))
);
const RaisedButton = Loadable(
  lazy(() => import("../components/buttons/raisedButton"))
);

// Forms
const FormValidation = Loadable(
  lazy(() => import("../components/forms/form-control/form-validation"))
);
const AffiliationCompaniesTest = Loadable(
  lazy(() =>
    import("../components/forms/form-control/AffiliationsCompaniesTest")
  )
);
const BaseInput = Loadable(
  lazy(() => import("../components/forms/form-control/baseInput"))
);
const InputGroup = Loadable(
  lazy(() => import("../components/forms/form-control/inputGroup"))
);
const MegaOption = Loadable(
  lazy(() => import("../components/forms/form-control/megaOption"))
);
const CheckboxandRadio = Loadable(
  lazy(() => import("../components/forms/form-control/checkboxandRadio"))
);

// Form Layout
const FormDefault = Loadable(
  lazy(() => import("../components/forms/form-layout/formDefault"))
);
const FormWizard1 = Loadable(
  lazy(() =>
    import("../components/forms/form-layout/form-wizard-1/formwizard1")
  )
);

// Form Widgets
const Datepicker = Loadable(
  lazy(() => import("../components/forms/form-widget/datepicker"))
);
const Timepicker = Loadable(
  lazy(() =>
    import("../components/forms/form-widget/timepickerComponent/timepicker")
  )
);
const Typeahead = Loadable(
  lazy(() =>
    import("../components/forms/form-widget/typeaheadComponent/typeahead")
  )
);

// Tables
const BasicTabels = Loadable(
  lazy(() => import("../components/tables/basicTable"))
);
const BorderTable = Loadable(
  lazy(() => import("../components/tables/borderTable"))
);
const SizingTable = Loadable(
  lazy(() => import("../components/tables/sizingTable"))
);
const StylingTable = Loadable(
  lazy(() => import("../components/tables/stylingTable"))
);
const DataTable = Loadable(
  lazy(() => import("../components/tables/dataTable"))
);

// Charts
const Apexcharts = Loadable(
  lazy(() => import("../components/charts/apexCharts"))
);
const GoogleCharts = Loadable(
  lazy(() => import("../components/charts/googleCharts"))
);
const KnobChart = Loadable(
  lazy(() => import("../components/charts/knobChart"))
);
const Chartist = Loadable(
  lazy(() => import("../components/charts/chartistCharts"))
);

// Gallery
const ImageGallery = Loadable(
  lazy(() => import("../components/gallery/imageGallery"))
);
const ImageWithDesc = Loadable(
  lazy(() => import("../components/gallery/imageWithDesc"))
);
const MesonryGallery = Loadable(
  lazy(() => import("../components/gallery/mesonryGallery"))
);
const MesonryDesc = Loadable(
  lazy(() => import("../components/gallery/mesonryDesc"))
);
const ImageHover = Loadable(
  lazy(() => import("../components/gallery/imageHover"))
);

// Blog
const BlogDetail = Loadable(
  lazy(() => import("../components/blog/blogDetail"))
);
const BlogSingle = Loadable(
  lazy(() => import("../components/blog/blogSingle"))
);
const BlogPost = Loadable(lazy(() => import("../components/blog/blogPost")));

// Job Search
const CardView = Loadable(
  lazy(() => import("../components/jobSearch/cardView"))
);
const JobList = Loadable(
  lazy(() => import("../components/jobSearch/job-list"))
);
const JobDetail = Loadable(
  lazy(() => import("../components/jobSearch/job-detail"))
);
const JobApply = Loadable(
  lazy(() => import("../components/jobSearch/job-apply"))
);

// Learning
const Learninglist = Loadable(
  lazy(() => import("../components/learning/learning-list"))
);
const LearningDeatil = Loadable(
  lazy(() => import("../components/learning/learning-deatil"))
);

// Calendar
const BasicCalender = Loadable(
  lazy(() => import("../components/calendar/basicCalendar"))
);
const DraggableCalendar = Loadable(
  lazy(() => import("../components/calendar/draggableCalendar"))
);

// Users
const UserProfile = Loadable(
  lazy(() => import("../components/users/userProfile"))
);
const UserEdit = Loadable(lazy(() => import("../components/users/userEdit")));
const UserCards = Loadable(lazy(() => import("../components/users/userCards")));

// Maps
const GoogleMap = Loadable(lazy(() => import("../components/map/googleMap")));

// Editor
const CkEditor = Loadable(lazy(() => import("../components/editor/ckEditor")));
const MdeEditor = Loadable(
  lazy(() => import("../components/editor/mdeEditor"))
);

// Social App
const SocialApp = Loadable(lazy(() => import("../components/social-app")));

// FAQ page
const FaqComponent = Loadable(lazy(() => import("../components/faq")));

// Knowledgebase page
const KnowledgebaseComponent = Loadable(
  lazy(() => import("../components/knowledgebase"))
);

// Support Ticket
const SupportTicket = Loadable(
  lazy(() => import("../components/support-ticket"))
);

// Sample page
const Sample = Loadable(lazy(() => import("../components/sample")));
const Playground = Loadable(
  lazy(() => import("../components/sample/playground"))
);
const MisReports = Loadable(
  lazy(() => import("../components/reports/mis-cron"))
);

// Search page
const Search = Loadable(lazy(() => import("../components/search")));

// E-commerce app
const Product = Loadable(
  lazy(() => import("../components/application/ecommerce-app/product"))
);
const ProductDetail = Loadable(
  lazy(() => import("../components/application/ecommerce-app/productpage"))
);
const Cart = Loadable(
  lazy(() => import("../components/application/ecommerce-app/cart"))
);
const Wishlist = Loadable(
  lazy(() => import("../components/application/ecommerce-app/wishlist"))
);
const Productlist = Loadable(
  lazy(() => import("../components/application/ecommerce-app/productlist"))
);
const Paymentdetails = Loadable(
  lazy(() => import("../components/application/ecommerce-app/paymentdetails"))
);
const OrderHistory = Loadable(
  lazy(() => import("../components/application/ecommerce-app/orderHistory"))
);
const Checkout = Loadable(
  lazy(() => import("../components/application/ecommerce-app/checkout"))
);
const Invoice = Loadable(
  lazy(() => import("../components/application/ecommerce-app/invoice"))
);
const Pricing = Loadable(
  lazy(() => import("../components/application/ecommerce-app/pricing"))
);

// EPortal2.0 Routes
const SaleCategory = Loadable(
  lazy(() => import("../components/sales/categories/categories"))
);
const Products = Loadable(
  lazy(() => import("../components/sales/products/products"))
);
const ModelProducts = Loadable(
  lazy(() => import("../components/sales/products/ModelProducts"))
);
const Services = Loadable(
  lazy(() => import("../components/sales/services/services"))
);
const Coupons = Loadable(
  lazy(() => import("../components/sales/coupons/coupons"))
);
const Policies = Loadable(
  lazy(() => import("../components/sales/policies/policies"))
);
const Certificate = Loadable(
  lazy(() => import("../components/sales/policies/certificate"))
);
const CommissionInvoice = Loadable(
  lazy(() => import("../components/sales/policies/invoice"))
);
const DealershipClaim = Loadable(
  lazy(() => import("../components/sales/policies/dealership-claim"))
);
const Certificate2 = Loadable(
  lazy(() => import("../components/sales/policies/certificate2"))
);
const SearchCertificates = Loadable(
  lazy(() => import("../components/sales/certificates/search-certificates"))
);
const SearchPayUEntries = Loadable(
  lazy(() => import("../components/affiliations/clients/dealerships/payu/payu-entrires"))
);
const Customers = Loadable(
  lazy(() => import("../components/sales/policies/customers"))
);
const Brands = Loadable(
  lazy(() => import("../components/affiliations/manufacturers/brands/brands"))
);
const Models = Loadable(
  lazy(() => import("../components/affiliations/manufacturers/models/models"))
);
const Variants = Loadable(
  lazy(() =>
    import("../components/affiliations/manufacturers/variants/variants")
  )
);
const Fuels = Loadable(
  lazy(() => import("../components/affiliations/manufacturers/fuels/fuels"))
);
const Transmissions = Loadable(
  lazy(() =>
    import(
      "../components/affiliations/manufacturers/transmissions/transmissions"
    )
  )
);
const ZoneMaster = Loadable(
  lazy(() =>
    import("../components/affiliations/clients/zone_master/zone_master")
  )
);
const RegionMaster = Loadable(
  lazy(() =>
    import("../components/affiliations/clients/region_master/region_master")
  )
);
const Companies = Loadable(
  lazy(() => import("../components/affiliations/clients/companies/companies"))
);

const GlobalSync = Loadable(
  lazy(() => import("../components/affiliations/globalConfiguration/globalsync"))
);

const CompaniesBasicDetails = Loadable(
  lazy(() =>
    import(
      "../components/affiliations/clients/companies/modals/add-company/basic-details"
    )
  )
);
const CompaniesEditBasicDetails = Loadable(
  lazy(() =>
    import(
      "../components/affiliations/clients/companies/modals/edit-company/basic-details"
    )
  )
);
const ClientConfiguration = Loadable(
  lazy(() =>
    import(
      "../components/affiliations/clients/companies/modals/add-company/ClientConfiguration"
    )
  )
);
const Dealerships = Loadable(
  lazy(() =>
    import("../components/affiliations/clients/dealerships/dealerships")
  )
);
const DealershipsForMisRole = Loadable(
  lazy(() =>
    import(
      "../components/affiliations/clients/dealerships/dealershipsForMisRole.jsx"
    )
  )
);
const DealerCodeMapping = Loadable(
  lazy(() =>
    import(
      "../components/affiliations/clients/dealerCodeMapping/dealer-code-mapping"
    )
  )
);
const AdminWallet = Loadable(
  lazy(() => import("../components/navision/admin_wallet"))
);
// import WalletRefundList from "../components/wallet/wallet-refund";
const WalletRefundList = Loadable(
  lazy(() => import("../components/wallet/WalletRefundList"))
);
const WalletRefund = Loadable(
  lazy(() => import("../components/navision/wallet_refund"))
);
const WalletMap = Loadable(
  lazy(() => import("../components/navision/WalletMap"))
);
const Users = Loadable(
  lazy(() => import("../components/user_management/users/users"))
);
const PageComingSoon = Loadable(
  lazy(() => import("../pages/page-coming-soon"))
);
const Roles = Loadable(
  lazy(() => import("../components/user_management/acl/roles/roles"))
);
const Permissions = Loadable(
  lazy(() =>
    import("../components/user_management/acl/permissions/permissions")
  )
);
const RolePermissions = Loadable(
  lazy(() =>
    import(
      "../components/user_management/acl/role_permissions/role_permissions"
    )
  )
);
const UserRoles = Loadable(
  lazy(() => import("../components/user_management/acl/user_roles/user_roles"))
);
const StaticCommission = Loadable(
  lazy(() => import("../components/commissions/static/static"))
);
const IncentivizedCommission = Loadable(
  lazy(() => import("../components/commissions/incentivized/incentivized"))
);
const Status = Loadable(
  lazy(() => import("../components/commissions/status/status"))
);
const SalesReport = Loadable(
  lazy(() => import("../components/reports/sales/sales-report"))
);
const PolicyReport = Loadable(
  lazy(() => import("../components/reports/sales/PolicyReport.jsx"))
);
const PolicyReportForMisRole = Loadable(
  lazy(() => import("../components/reports/sales/PolicyReportForMisRole.jsx"))
);
const MISReport = Loadable(
  lazy(() => import("../components/reports/sales/MISReport.jsx"))
);
const MISReportForMSIRole = Loadable(
  lazy(() => import("../components/reports/sales/MISReportForMisRole.jsx"))
);
const DealerKYC = Loadable(
  lazy(() => import("../components/reports/sales/Dealer-KYC-Reports"))
);
const DealerMISReport = Loadable(
  lazy(() => import("../components/reports/sales/dealer-mis-report"))
);
const RsaSalesReport = Loadable(
  lazy(() => import("../components/reports/sales/rsa-sales-report"))
);
const TargetAllotment = Loadable(
  lazy(() => import("../components/reports/sales/target-allotment"))
);
const TargetVsAchievement = Loadable(
  lazy(() => import("../components/reports/sales/target-vs-achievement"))
);
const AdminWalletReport = Loadable(
  lazy(() => import("../components/reports/wallet/admin-wallet-report"))
);
const UserWalletReport = Loadable(
  lazy(() => import("../components/reports/wallet/user-wallet-report"))
);
const AccessLogs = Loadable(
  lazy(() => import("../components/logs/server/access-log/access-log"))
);
const PolicyElastic = Loadable(
  lazy(() => import("../components/logs/server/policy"))
);
const ErrorLogs = Loadable(
  lazy(() => import("../components/logs/server/error-log/error-log"))
);
const CronLogs = Loadable(
  lazy(() => import("../components/logs/server/cron-log/cron-log"))
);
const APILogs = Loadable(
  lazy(() => import("../components/logs/api/api-log/api-log"))
);
const ActivityLogs = Loadable(
  lazy(() => import("../components/logs/api/activity-log/activity-log"))
);
const DSP = Loadable(
  lazy(() => import("../components/affiliations/clients/dsp/dsp"))
);
const MyAccount = Loadable(
  lazy(() => import("../components/account/my-account"))
);
const CommissionInvoiceStatus = Loadable(
  lazy(() => import("../components/dealerCommission/invoice-status"))
);
const DealerCommissionInvoiceStatus = Loadable(
  lazy(() => import("../components/dealerCommission/dealer-invoice-status"))
);
const ChangePassword = Loadable(
  lazy(() => import("../components/account/change-password"))
);
const ProductsAssign = Loadable(
  lazy(() => import("../components/sales/products/products-assign"))
);
const PolicyCreate = Loadable(
  lazy(() => import("../components/sales/policies/policy-create/policy-create"))
);
const PolicyCreateWithStepper = Loadable(
  lazy(() =>
    import(
      "../components/sales/policies/policy-create/PolicyCreateWithStepper.jsx"
    )
  )
);

const BulkPolicyUpload = Loadable(
  lazy(() =>
    import("../components/sales/policies/policy-create/bulk-upload-policy")
  )
);
const EditTargetAllotment = Loadable(
  lazy(() => import("../components/reports/sales/edit-target-allotment.jsx"))
);
const CompanyUsers = Loadable(
  lazy(() =>
    import("../components/affiliations/clients/companies/company-users")
  )
);
const DealershipUsers = Loadable(
  lazy(() =>
    import("../components/affiliations/clients/dealerships/dealership-users")
  )
);
const BulkUploadDealership = Loadable(
  lazy(() =>
    import(
      "../components/affiliations/clients/dealerships/modals/bulk-upload-dealerships"
    )
  )
);
const Helpdesk = Loadable(
  lazy(() => import("../components/documents/helpdesk"))
);
const KYC = Loadable(lazy(() => import("../components/documents/kyc/kyc")));
const Viewhelpdesk = Loadable(
  lazy(() => import("../components/documents/view-helpdesk.jsx"))
);
const ELearningCourses = Loadable(
  lazy(() => import("../components/training/e-learning/operations/courses"))
);
const ELearningCourseSections = Loadable(
  lazy(() => import("../components/training/e-learning/operations/sections"))
);
const ELearningPlay = Loadable(
  lazy(() => import("../components/training/e-learning/operations/play"))
);
const ELearningQuiz = Loadable(
  lazy(() => import("../components/training/e-learning/operations/quiz"))
);
const ECourseModule = Loadable(
  lazy(() =>
    import("../components/training/e-learning/operations/courses_module")
  )
);
const ECourseModuleTopic = Loadable(
  lazy(() =>
    import("../components/training/e-learning/operations/courses-topic")
  )
);
const ECourseModuleQuiz = Loadable(
  lazy(() =>
    import("../components/training/e-learning/operations/courses-quiz.jsx")
  )
);
const ManageCourse = Loadable(
  lazy(() => import("../components/training/e-learning/manage/manage-course"))
);
const ManageCourseSections = Loadable(
  lazy(() => import("../components/training/e-learning/manage/manage-section"))
);
const MyRequests = Loadable(
  lazy(() => import("../components/requests/my-requests"))
);
const AdminRequests = Loadable(
  lazy(() => import("../components/requests/admin-requests"))
);
const RequestsForMe = Loadable(
  lazy(() => import("../components/requests/requests-for-me"))
);
const RequestsForMeByRole = Loadable(
  lazy(() => import("../components/requests/requests-for-me-by-role"))
);
const MyRequestsVisit = Loadable(
  lazy(() => import("../components/requests/my-request-visit"))
);
const UploadImageToS3 = Loadable(
  lazy(() => import("../components/requests/upload-image-to-s3"))
);
const WalletCredits = Loadable(
  lazy(() => import("../components/wallet/user/credits"))
);
// const LoginWithBgImage = Loadable(lazy(() => import('../pages/authentication/loginWithBgImage')));
const DMSData = Loadable(
  lazy(() => import("../components/affiliations/clients/dms_data/dms_data"))
);
const FestiveGreetings = Loadable(
  lazy(() =>
    import("../components/affiliations/festiveGreetings/festive_greetings")
  )
);
const DMSList = Loadable(
  lazy(() => import("../components/affiliations/clients/dms_data/dms_list"))
);
const UserWalletStatement = Loadable(
  lazy(() => import("../components/wallet/user/statement"))
);
const Countries = Loadable(
  lazy(() => import("../components/sales/taxes/countries/countries"))
);
const States = Loadable(
  lazy(() => import("../components/sales/taxes/states/states"))
);
const ParentTaxes = Loadable(
  lazy(() => import("../components/sales/taxes/parent-taxes/parent-taxes"))
);
const SubTaxes = Loadable(
  lazy(() => import("../components/sales/taxes/sub-taxes/sub-taxes"))
);
const BdmSalesReport = Loadable(
  lazy(() => import("../components/reports/Bdm-sales/sales-report"))
);
const BdmTable = Loadable(
  lazy(() => import("../components/reports/Bdm-sales/Bdm-table"))
);
const BdmPolicies = Loadable(
  lazy(() => import("../components/sales/bdm/bdm-product"))
);
const ProductLists = Loadable(
  lazy(() => import("../components/sales/bdm/product-List"))
);
const AddUser = Loadable(
  lazy(() => import("../components/user_management/users/AddUser"))
);

// Email
const Email = Loadable(
  lazy(() => import("../components/application/email-app/emailDefault"))
);

// Chat
const Chat = Loadable(lazy(() => import("../components/application/chat-app")));

// Bookmark
const Bookmark = Loadable(
  lazy(() => import("../components/application/bookmark"))
);

// Project app
const ProjectList = Loadable(
  lazy(() => import("../components/application/project/project"))
);
const NewProject = Loadable(
  lazy(() => import("../components/application/project/new-project"))
);

// File Manager App
const FileManager = Loadable(
  lazy(() => import("../components/application/file-manager/file-manager"))
);

// Kanban Board
const kanbanBoard = Loadable(
  lazy(() => import("../components/application/kanban-board/kanbanBoard"))
);

// Task
const TaskApp = Loadable(
  lazy(() => import("../components/application/task-app"))
);

// Contact app
const ContactApp = Loadable(
  lazy(() => import("../components/application/contact-app"))
);

// todo-app
const Todo = Loadable(lazy(() => import("../components/application/todo-app")));

// todo-firebase-app
const TodoFirebase = Loadable(
  lazy(() => import("../components/application/todo-firebase-app"))
);

// Session
const Session = Loadable(
  lazy(() => import("../components/user_management/session"))
);
const AutomatedEmail = Loadable(
  lazy(() => import("../components/user_management/automated_email"))
);
const BirthdayLogs = Loadable(
  lazy(() =>
    import("../components/user_management/BirthdayLogs/birthday_logs.jsx")
  )
);
const VisitingReport = Loadable(
  lazy(() => import("../components/visiting/Visiting.jsx"))
);

const IndiaMap = Loadable(
  lazy(() =>
    import("../components/required-charts/indiaMapChart/india-map-chart")
  )
);

const GetOtp = Loadable(
  lazy(() => import("../components/user_management/getOtp"))
);

const TargetAllotmentClient = Loadable(
  lazy(() => import("../components/targetAllotment/targetAllotmentClient"))
);

const MonthlyTargetDestribution = Loadable(
  lazy(() => import("../components/targetAllotment/monthlyTargetDestribution"))
);

const SetTarget = Loadable(
  lazy(() => import("../components/targetAllotment/setTarget"))
);

const IncentiveReport = Loadable(
  lazy(() => import("../components/targetAllotment/incentiveReport"))
);

const ZMIncentiveReport = Loadable(
  lazy(() => import("../components/targetAllotment/zmIncentiveReport"))
);

const BulkPolicyUploadHero = Loadable(
  lazy(() => import("../components/sales/policies/policy-create/bulk-upload-policy-hero"))
);

// NCB / Dealer Qr Policy Punch
const DealerQRCode = Loadable(
  lazy(() => import("../components/user_policy_punch/dealer-qrcode"))
);

const SelfPolicyPunch = Loadable(
  lazy(() => import("../components/user_policy_punch/self-policy-punch"))
);
const FreshPolicyPunch = Loadable(
  lazy(() => import("../components/user_policy_punch/fresh-policy-punch"))
);
const FreshPolicyPunchResult = Loadable(
  lazy(() => import("../components/user_policy_punch/fresh-policy-punch-result"))
);

export default ApplicationRoutes;

